import {Button, Card, CardActions, CardContent, Grid, MenuItem, Stack, TextField, Typography} from "@mui/material";
import * as React from "react";
import {useContext, useEffect, useState} from "react";
import FormTextField from "../../../experiments/forms/FormTextField";
import {SubmitHandler, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {useTranslation} from "react-i18next";
import * as Yup from "yup";
import FormSelect from "../../../experiments/forms/FormSelect";
import {ISelectValues} from "../../cashback/constants/ISelectValues";
import {useNavigate, useParams} from "react-router-dom";
import FormDatePicker from "../../../experiments/forms/FormDatePicker";
import dayjs from "dayjs";
import {useCBRepository} from "../../../api/repositories/CBRepository";
import FormAsyncAutocomplete from "../../../experiments/forms/FormAsyncAutocomplete";
import {EditRounded} from "@mui/icons-material";
import useToggle from "../hooks/useToggle";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {setSnackbar} from "../../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../../util/state/globalVariables";
import {NETWORK_CODE, STATUS_ACTIVE, STATUS_READY, STATUS_REVOKED} from "../constants/CardConstants";
import CardsModal from "./CardsModal";
import {useCardsRepository} from "../../../api/repositories/AmexCardsRepository";
import {useSetRecoilState} from "recoil";
import {STATE_ACTIVE_FILTERS} from "../constants/CardRecoil";
import {useHasScope} from "../../../../../config/globalConfig";
import {
    AMEXSOF_DELETE_CARD,
    AMEXSOF_EDIT_CARD,
    AMEXSOF_REACTIVE_CARD,
    AMEXSOF_REVOKE_CARD
} from "../../../utils/FinanceScope";

function CardsWizard(props: { type: string }) {
    const {t} = useTranslation("finance", {keyPrefix: "payments.amex.cards"});
    const {t: tg} = useTranslation("translation");

    const {dispatchGlobalData, globalData: {user}} = useContext(GlobalContext);
    const {getCard, postCard, putCard, putCardStatus, deleteCard} = useCardsRepository()
    const {getCustomers} = useCBRepository()
    const setFilters = useSetRecoilState(STATE_ACTIVE_FILTERS);
    const navigate = useNavigate();
    const {id} = useParams();
    const hasScope = useHasScope;
    const [editable, setEditable] = useState<boolean>(props.type !== 'UPDATE');
    const [card, setCard] = useState<any>();

    const [showUnsaved, toggleUnsaved] = useToggle();
    const [showDelete, toggleDelete] = useToggle();
    const [showRevoke, toggleRevoke] = useToggle();
    const [showReactive, toggleReactive] = useToggle();
    const legalEntityOptions: any[] = user.legalEntities
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`legalEntities.${x.name}`),
        }));


    const dealerOptions: any[] = user.dealers
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`dealers.${x.name}`),
        }));
    const [dealers, setDealers] = useState<any[]>(dealerOptions);
    const schema = Yup.object().shape({
        expirationDate: Yup.date().required().typeError(t('pages.wizard.form.error-expiration')),
        networkCode: Yup.string().required(),
        customerSapId: Yup.string().test('invalid-sapId', t("filters.errors.invalid-sapid"), function (value) {
            return !!value && value.length > 0;
        }),
        pan: Yup.string().when('networkCode', {
            is: '06',
            then: Yup.string().required(t('pages.wizard.form.error-required-field', { field: t('pages.wizard.form.label-pan') }))
        }).nullable(),
        panAliasCustomer: Yup.string().when('networkCode', {
            is: '95',
            then: Yup.string().required(t('pages.wizard.form.error-required-field', { field: t('pages.wizard.form.label-customer-pan-alias') }))
        }).nullable(),
        panAlias: Yup.string().when('networkCode', {
            is: '98',
            then: Yup.string().required(t('pages.wizard.form.error-required-field', { field: t('pages.wizard.form.label-pan-alias') }))
        }).nullable(),

    });

    const defaultValues = {
        customerSapId: "",
        pan: null,
        panAlias: null,
        panAliasCustomer: null,
        networkCode: "",
        expirationDate: null,
        customerNameOrCompany: "",
        status: ""
    };

    const {control, setValue, watch, handleSubmit, getValues, formState, trigger} = useForm<any>({
        defaultValues: defaultValues,
        resolver: yupResolver(schema)
    });

    useEffect(() => {
        let dirty = formState.isDirty;
    }, [formState])

    const watcher = watch();

    const onSubmit: SubmitHandler<any> = (data) => {

        data.pan = data.pan?.trim() === "" ? null : data.pan;
        data.panAlias = data.panAlias?.trim() === "" ? null : data.panAlias;
        data.panAliasCustomer = data.panAliasCustomer?.trim() === "" ? null : data.panAliasCustomer;

        if (props.type === 'CREATE') {
            data['customerNameOrCompany'] = data["customer"]["value"]["name"];
            delete data["customer"];

            postCard(data).then((response) => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('snackbar.success-create'),
                            type: snackbarTypes.success,
                            show: true,
                            autoClose: true
                        }
                    }
                });
                navigate(`/finance/payments/amex/cards`);
            }).catch(() => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('snackbar.error-create'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true
                        }
                    }
                });
            });

        } else if (props.type === 'UPDATE') {
            data['id'] = id;
            putCard(data).then((response) => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('snackbar.success-update'),
                            type: snackbarTypes.success,
                            show: true,
                            autoClose: true
                        }
                    }
                });
                navigate(`/finance/payments/amex/cards`);
            }).catch(() => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('snackbar.error-update'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true
                        }
                    }
                });
            });
        }

    }

    function changeStatus(status: string): any {
        card.status = status
        putCardStatus(card).then((response) => {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('snackbar.success-update'),
                        type: snackbarTypes.success,
                        show: true,
                        autoClose: true
                    }
                }
            });
            navigate(`/finance/payments/amex/cards`);
        }).catch(() => {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('snackbar.error-update'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                }
            });
        });
    }

    function delCard(): any {
        deleteCard(card).then((response) => {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('snackbar.success-delete'),
                        type: snackbarTypes.success,
                        show: true,
                        autoClose: true
                    }
                }
            });
            navigate(`/finance/payments/amex/cards`);
        }).catch(() => {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('snackbar.error-delete'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                }
            });
        });
    }

    function checkCancel() {
        if (formState.isDirty) {
            toggleUnsaved();
        } else {
            setFilters([]);
            navigate(`/finance/payments/amex/cards`)
        }
    }

    const handleChangeLegalEntities = (value: any) => {
        if (value == '') {
            setDealers([]);
            return;
        }
        let dealers = user?.dealers
            ?.filter(
                (x: any) =>
                    x.legalEntities.some((e: any) => value == e.name) &&
                    x.name.toLowerCase() !== "all"
            )
            .map((x: any) => {
                return {
                    label: tg(`dealers.${x.name}`),
                    value: x.name,
                };
            });
        setDealers(dealers);
    }

    useEffect(() => {
        if (props.type === 'UPDATE' && id !== undefined) {
            getCard(id).then((response) => {
                setCard(response.result);
                let data = response.result;
                setValue('customerNameOrCompany', data.customerNameOrCompany);
                setValue('status', data.status);
                setValue('customerSapId', data.customerSapId);
                setValue('networkCode', data.networkCode);
                setValue('pan', data.pan);
                setValue('panAlias', data.panAlias);
                setValue('panAliasCustomer', data.panAliasCustomer);
                setValue('expirationDate', data.expirationDate);
            })
        }
    }, [id])

    function cancel() {
        navigate(-1)
    }

    return (
        <Card variant="outlined" sx={{margin: '16px'}}>

            {showUnsaved &&
                <CardsModal
                    open={showUnsaved}
                    title={t("modal.unsavedChanges")}
                    body={t("modal.unsavedBodyMessage")}
                    leftButton={t("modal.actionButtons.discard")}
                    leftFunction={cancel}
                    rightButton={t("modal.actionButtons.editing")}
                    rigthFunction={toggleUnsaved}
                    onClose={toggleUnsaved}

                />
            }
            {showDelete &&
                <CardsModal
                    open={showDelete}
                    title={t("modal.deletedChanges")}
                    body={t("modal.deletedBodyMessage", {customerName: card.customerNameOrCompany})}
                    leftButton={t("modal.actionButtons.cancel")}
                    leftFunction={toggleDelete}
                    rightButton={t("modal.actionButtons.delete")}
                    rigthFunction={() => delCard()}
                    onClose={toggleDelete}

                />
            }
            {showRevoke &&
                <CardsModal
                    open={showRevoke}
                    title={t("modal.revokedChanges")}
                    body={t("modal.revokedBodyMessage", {customerName: card.customerNameOrCompany})}
                    leftButton={t("modal.actionButtons.cancel")}
                    leftFunction={toggleRevoke}
                    rightButton={t("modal.actionButtons.revoke")}
                    rigthFunction={() => changeStatus(STATUS_REVOKED)}
                    onClose={toggleRevoke}

                />
            }
            {showReactive &&
                <CardsModal
                    open={showReactive}
                    title={t("modal.reactiveChanges")}
                    body={t("modal.reactiveBodyMessage", {customerName: card.customerNameOrCompany})}
                    leftButton={t("modal.actionButtons.cancel")}
                    leftFunction={toggleReactive}
                    rightButton={t("modal.actionButtons.reactive")}
                    rigthFunction={() => changeStatus(STATUS_ACTIVE)}
                    onClose={toggleReactive}

                />
            }

            <form onSubmit={handleSubmit(onSubmit)}>
                <CardContent>
                    <Grid container direction={'row'} rowGap={5}>
                        {showCardHeader()}
                        <Grid container item spacing={2} alignItems={'center'}>
                            <Grid item xs={12}>
                                <Typography variant={'body2'} color={'primary.light'} fontWeight={700}
                                            textTransform={'uppercase'}>
                                    {id ? t('pages.wizard.form.title-all-detail') : t('pages.wizard.form.title-all-create')}
                                </Typography>
                            </Grid>
                            {props.type === 'UPDATE' &&
                                <Grid item xs={12}>
                                    <TextField
                                        name={`status`}
                                        value={getValues('status')}
                                        disabled={true}
                                    />
                                </Grid>
                            }
                            <Grid item md={3} sm={3} xs={12}>
                                {(props.type === 'CREATE') &&
                                    <FormAsyncAutocomplete
                                        name={'customer'}
                                        control={control}
                                        props={{
                                            callback: (it) => getCustomers(it),
                                            onSelect: (option) => {
                                                setValue('customerSapId', option?.value?.sapId)
                                                trigger('customerSapId')
                                            },
                                            disabled: !editable,
                                            getOptionLabel: (option) => option?.value?.name,
                                            renderInput: (params) =>
                                                <TextField
                                                    {...params}
                                                    label={t('pages.wizard.form.label-customer-search-name')}
                                                />
                                        }}
                                    />
                                }
                                {(props.type === 'UPDATE') &&
                                    <FormTextField
                                        control={control}
                                        name={'customerNameOrCompany'}
                                        props={{
                                            disabled: true,
                                            label: t('pages.wizard.form.label-customer-search-name'),
                                            sx: {width: '100%'},
                                        }}
                                    />
                                }
                            </Grid>
                            <Grid item md={3} sm={3} xs={12}>
                                <FormTextField
                                    control={control}
                                    name={'customerSapId'}
                                    props={{
                                        disabled: true,
                                        label: t('pages.wizard.form.label-customer-sap-id'),
                                        sx: {width: '100%'},
                                    }}
                                />
                            </Grid>
                            {(props.type === 'CREATE') &&
                                <>
                                    <Grid item md={3} sm={3} xs={12}>
                                        <FormSelect
                                            name={"legalEntity"}
                                            control={control}
                                            props={{
                                                label: tg("shared.legalEntity"),
                                                fullWidth: true,
                                                required: true,
                                                onChange: (e: any) => {
                                                    handleChangeLegalEntities(e.target.value)
                                                },
                                            }}

                                            items={legalEntityOptions.map((option: any) => (
                                                <MenuItem key={option.value}
                                                          value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        />
                                    </Grid>
                                    <Grid item md={3} sm={3} xs={12}>
                                        <FormSelect
                                            name={"dealer"}
                                            control={control}
                                            props={{
                                                label: tg("shared.dealer"),
                                                fullWidth: true,
                                                required: true,
                                                disabled: dealers.length == 0
                                            }}
                                            items={dealers.map((option: any) => (
                                                <MenuItem key={option.value}
                                                          value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        />
                                    </Grid>
                                </>
                            }
                            <Grid item md={3} sm={6} xs={12}>
                                <FormSelect
                                    name={"networkCode"}
                                    control={control}
                                    props={{
                                        disabled: !editable,
                                        variant: "standard",
                                        fullWidth: true,
                                        label: t('pages.wizard.form.label-network-code')
                                    }}
                                    items={NETWORK_CODE.map((item: ISelectValues, index: number) => (
                                        <MenuItem key={index} value={item.value}>
                                            {t(item.label)}
                                        </MenuItem>
                                    ))}/>
                            </Grid>
                            <Grid item md={3} sm={6} xs={12}>
                                <FormTextField
                                    control={control}
                                    name={'pan'}
                                    props={{
                                        disabled: !editable,
                                        label: t('pages.wizard.form.label-pan'),
                                        sx: {width: '100%'},
                                        InputLabelProps: { shrink:  !!watch("pan") },
                                    }}
                                />
                            </Grid>
                            <Grid item md={3} sm={6} xs={12}>
                                <FormTextField
                                    control={control}
                                    name={'panAlias'}
                                    props={{
                                        disabled: !editable,
                                        label: t('pages.wizard.form.label-pan-alias'),
                                        sx: {width: '100%'},
                                        InputLabelProps: { shrink:  !!watch("panAlias") },
                                    }}
                                />
                            </Grid>
                            <Grid item md={3} sm={6} xs={12}>
                                <FormTextField
                                    control={control}
                                    name={'panAliasCustomer'}
                                    props={{
                                        disabled: !editable,
                                        label: t('pages.wizard.form.label-customer-pan-alias'),
                                        sx: {width: '100%'},
                                        InputLabelProps: { shrink: !!watch("panAliasCustomer") },
                                    }}
                                />
                            </Grid>
                            <Grid item md={3} sm={6} xs={12}>
                                <FormDatePicker
                                    control={control}
                                    name={'expirationDate'}
                                    props={{
                                        disabled: !editable,
                                        label: t('pages.wizard.form.label-expiration'),
                                        minDate: dayjs().toDate(),
                                    }}
                                />
                            </Grid>

                        </Grid>

                    </Grid>
                </CardContent>
                <CardActions sx={{justifyContent: 'flex-end'}}>
                    {showCardActions()}
                </CardActions>
            </form>
        </Card>

    );

    function showCardActions() {
        let status = card?.status?.toUpperCase();
        if (props.type === 'CREATE') {
            return (
                <>
                    <Button variant={"outlined"} color={"accent"}
                            onClick={() => checkCancel()}>{t('pages.wizard.buttons.cancel')}</Button>
                    <Button color={"accent"} type="submit">{t('pages.wizard.buttons.create')}</Button>
                </>
            );
        }
        if (props.type === 'UPDATE') {
            if (editable) {
                return (
                    <>
                        <Button variant={"outlined"} color={"accent"}
                                onClick={() => checkCancel()}>{t('pages.wizard.buttons.cancel')}</Button>
                        <Button color={"accent"} type="submit">{t('pages.wizard.buttons.save')}</Button>
                    </>
                );
            } else if (status === STATUS_READY && hasScope(AMEXSOF_DELETE_CARD)) {
                return (
                    <Button color={"accent"} onClick={toggleDelete}>
                        {t('pages.wizard.buttons.delete')}
                    </Button>
                );
            } else if (status === STATUS_ACTIVE && hasScope(AMEXSOF_REVOKE_CARD)) {
                return (
                    <Button color={"accent"} onClick={toggleRevoke}>
                        {t('pages.wizard.buttons.revoked')}
                    </Button>
                );
            } else if (status === STATUS_REVOKED && hasScope(AMEXSOF_REACTIVE_CARD)) {
                return (
                    <Button color={"accent"} onClick={toggleReactive}>
                        {t('pages.wizard.buttons.reactive')}
                    </Button>
                );
            }


        }
    }


    function showCardHeader() {
        let status = card?.status?.toUpperCase();
        if (props.type === 'UPDATE') {
            return (
                <Grid container item>
                    <Grid item xs={6}>
                        <Stack direction={'row'} alignItems={'center'}>
                            {/*<IconButton color={'primary'} onClick={() => checkCancel()}>*/}
                            {/*    <ArrowBackRounded />*/}
                            {/*</IconButton>*/}
                            <Typography variant={'body2'} color={'primary'} fontWeight={700}
                                        textTransform={'uppercase'}>
                                {t('arrowback.title', {name: watcher.customerNameOrCompany})}
                            </Typography>
                        </Stack>
                    </Grid>
                    {(status === STATUS_READY && hasScope(AMEXSOF_EDIT_CARD)) &&
                        <Grid item xs={6} display={'flex'} justifyContent={'flex-end'}>
                            <Button disabled={editable} variant={"text"} color={"accent"} startIcon={<EditRounded/>}
                                    onClick={() => {
                                        setEditable(true)
                                    }}>{t('pages.wizard.buttons.update')}</Button>
                        </Grid>
                    }
                </Grid>

            );
        }
    }
}

export default CardsWizard;